import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { fetchAll, fetchOne } from '../../lib/v31lib';
import { loginAtom } from '../../lib/auth';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import BusinessIcon from '@mui/icons-material/Business';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  Chip,
  Divider,
  Grid,
} from '@mui/material';

import {
  AnnAm,
  Dt,
  Page,
  StyledButton,
  StyledIconButton,
  StyledTextField,
  Typography
} from '../../components/interface';

import {
  Investment,
  InvestmentManager
} from '../../models';

// const { DateTime } = require("luxon");

type Props = {};

export const InvestmentsIndex: FunctionComponent<Props> = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [login, setLogin] = useAtom(loginAtom);
  const [perSortMngr, setPerSortMngr] = useState<boolean>(true);
  const [manualPrice, setManualPrice] = useState<boolean>(false);
  const [price, setPrice] = useState<string>('');
  const [activeInvestment, setActiveInvestment] = useState<Investment>();
  const investmentTypes:{[z:string]: string} = {
    'bonds': "Bonds",
    'real_estate': "Real Estate",
    // 'obligations': "Obligations",
    'equities': "Equities",
    'gold': "Gold & other commodities"
  };

  const {
    data: investments,
    // isLoading: bookYearsLoading,
    // isError: bookYearsError,
    // isSuccess: bookYearsSuccess,
    refetch: investmentsRefetch,
  } = useQuery({
    queryKey: ["investments"],
    queryFn: () =>
      fetchAll<Investment>(
        "/nl/v3/objects/fetch_all",
        {
          object: "investment",
          fields: [
            "id", 'created_at', 'name', 'investment_manager_id', 'isin', 'currency', 'original_purchase_price', 'amount',
            'investment_type', 'latest_price', 'investment_manager', 'show_url', 'latest_breakdown'
          ],
          sub_fields: {
            'investment_manager': ['id', 'name']
          },
          order: "name ASC",
          page: 0,
          per_page: 200
        },
        login
      ),
    enabled: !!login
  });

  const {
    data: investmentManagers,
    // isLoading: bookYearsLoading,
    // isError: bookYearsError,
    // isSuccess: bookYearsSuccess,
    // refetch: bookYearsRefetch,
  } = useQuery({
    queryKey: ["investment_managers"],
    queryFn: () =>
      fetchAll<InvestmentManager>(
        "/nl/v3/objects/fetch_all",
        {
          object: "investment_manager",
          fields: ["id", 'created_at', 'name'],
          order: "name ASC",
          page: 0,
          per_page: 200
        },
        login
      ),
    enabled: !!login
  });










  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetchOne<any>(
        "/nl/v3/objects/custom_action",
        pars.formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      investmentsRefetch();
      setActiveInvestment(undefined);
      setPrice('');
      setManualPrice(false);
    },
  });
  // const mutationExport = useMutation({
  //   mutationFn: () => {
  //     return fetchOne<RawExportStat>(
  //       "/nl/v3/objects/custom_action",
  //       {
  //         object: 'exit',
  //         class_action: 'custom_api_exit_stats_export',
  //         handler_id: login?.id,
  //         only_active: 0
  //       },
  //       login
  //     );
  //   },
  //   onSuccess: (data, variables, context) => {
  //     // setSuccessE1(true);
  //   },
  // });








  // useEffect(() => {
  //   getBookYears();
  // }, [login]);









  const sortingLines = (objects: any[]) => {
    return <>
      {objects.length < 1 && <Grid item xs={12}>
        <Typography variant="body1" sx={{
          fontStyle: 'italic',
          paddingLeft: 4,
          fontSize: '0.8rem'
        }}>No objects found...</Typography>
      </Grid>}
      {objects.map(inv => <>
        <Grid item xs={4} sx={{paddingLeft: "24px !important"}}>
          <Typography variant="body1">{inv.name}<br/><span style={{fontStyle: 'italic', fontSize: '0.8rem'}}>{inv.isin}</span></Typography>
          {!!perSortMngr && <Chip size="small" label={investmentTypes[inv.investment_type]} variant="outlined" sx={{marginRight: 1}} color="primary" />}
          {!perSortMngr && <Chip size="small" label={inv.investment_manager?.name} sx={{marginRight: 1}} color="primary" />}
        </Grid>
        <Grid item xs={1}>
          <AnnAm int amount={parseInt(inv.amount, 10)} />
        </Grid>
        <Grid item xs={2}>
          <AnnAm amount={parseFloat(inv.original_purchase_price || 0)} currency={inv.currency} />
        </Grid>
        <Grid item xs={2} sx={{
          textAlign: 'right',
          "& > p": {
            display: 'inline-block'
          }
        }}>
          <AnnAm
            amount={parseFloat(inv.latest_price?.price || 0)}
            base={parseFloat(inv.original_purchase_price || 0)}
            currency={inv.currency}
            simple
            date={inv.latest_price?.updated_at}
          />
          {!inv.show_url && <StyledIconButton color="primary" sx={{marginTop: -2}} onClick={() => {
            mutationCustomAction.mutate({
              formData: {
                object: 'investment',
                class_action: 'custom_api_sync',
                handler_id: login?.id,
                id: inv.id
              }
            });
          }}>
            <SyncIcon />
          </StyledIconButton>}
          {!!inv.show_url && <StyledIconButton color="primary" sx={{marginTop: -2}} onClick={() => {
            setManualPrice(true);
            setActiveInvestment(inv);
            window.scrollTo(0, 0);
          }}>
            <SyncProblemIcon />
          </StyledIconButton>}
        </Grid>
        <Grid item xs={2} sx={{textAlign: 'right'}}>
          <BusinessIcon sx={{marginRight: 1, color: (inv.latest_breakdown?.companies_data?.lines?.length > 0) ? theme.palette.tertiary?.main : theme.palette.secondary?.main, cursor: 'pointer'}} onClick={() => {
            navigate(`/internal/investment_breakdown/${inv.id}/${inv.latest_breakdown?.id}`);
          }} />
          <MedicalServicesIcon sx={{marginRight: 1, color: (inv.latest_breakdown?.sectors_data?.lines?.length > 0) ? theme.palette.tertiary?.main : theme.palette.secondary?.main, cursor: 'pointer'}} onClick={() => {
            navigate(`/internal/investment_breakdown/${inv.id}/${inv.latest_breakdown?.id}`);
          }} />
          <AssuredWorkloadIcon sx={{color: ((inv.latest_breakdown?.rating_data?.lines || []).filter((fff:string) => parseFloat(fff) > 0.0).length > 0) ? theme.palette.tertiary?.main : theme.palette.secondary?.main, cursor: 'pointer'}} onClick={() => {
            navigate(`/internal/investment_breakdown/${inv.id}/${inv.latest_breakdown?.id}`);
          }} />
          {!!inv.latest_breakdown?.updated_at && <Typography variant="body1" sx={{fontSize: '0.6rem'}}>
            <Dt d={inv.latest_breakdown?.updated_at} f="dd/LL/y t" />
          </Typography>}
        </Grid>
        <Grid item xs={1}>
          <StyledIconButton color="primary" path={`/internal/investment/${inv.id}`}>
            <VisibilityIcon />
          </StyledIconButton>
          <StyledIconButton color="primary" path={`/internal/investment_breakdown/${inv.id}/new`}>
            <BusinessIcon />
          </StyledIconButton>
        </Grid>
      </>)}
    </>;
  }

  return <Page sx={{}} title="Investments" helper="investments" actions={<>
    {!activeInvestment && <StyledButton
      label="Create new"
      id='create_new'
      contained
      onClick={() => {
        navigate("/internal/investment/new");
      }}
      sx={{marginRight: 3}}
      startIcon={<AddCircleOutlineIcon />}
    />}
    {!activeInvestment && <StyledButton
      label="Sync all"
      id='sync_all'
      loading={mutationCustomAction.isPending}
      contained
      onClick={() => {
        mutationCustomAction.mutate({
          formData: {
            object: 'investment',
            class_action: 'custom_api_sync_all',
            handler_id: login?.id
          }
        });
      }}
      sx={{marginRight: 3}}
      startIcon={<SyncIcon />}
    />}
  </>}>
    {!!activeInvestment && <Grid container spacing="8">
      <Grid item xs={6}>
        <Typography>Set a custom price for {activeInvestment.name} ({activeInvestment.isin}):</Typography>
        <StyledTextField
          label="Price"
          value={price || ''}
          id="custom_price"
          onChange={(v) => {
            setPrice(!!v ? v.replace(",", ".") : '');
          }}
          fullWidth
        />
        <StyledButton
          label="Cancel"
          id='cancel'
          // contained
          onClick={(v) => {
            setActiveInvestment(undefined);
            setPrice('');
            setManualPrice(false);
          }}
          // sx={{marginLeft: 1}}
        />
        <StyledButton
          label="Save"
          id='save'
          contained
          onClick={(v) => {
            mutationCustomAction.mutate({
              formData: {
                object: 'investment',
                class_action: 'custom_api_register_price',
                handler_id: login?.id,
                id: activeInvestment.id,
                price: price
              }
            });
          }}
          sx={{marginLeft: 1}}
        />
      </Grid>
      <Grid item xs={6}>
        <Typography sx={{
          textDecoration: 'underline',
          cursor: 'pointer'
        }} onClick={() => {
          window.open(activeInvestment.show_url, "_BLANK");
        }}>Open in new tab</Typography>
        <iframe style={{width: '100%', height: "600px"}} src={activeInvestment.show_url} />
      </Grid>
    </Grid>}





    {!activeInvestment && <Grid container spacing="8">
      <Grid item xs={12} sx={{
        marginBottom: 4
      }}>
        <StyledButton
          label="Per Manager"
          id='per_manager'
          contained={!!perSortMngr}
          onClick={() => {
            setPerSortMngr(true);
          }}
          sx={{marginRight: 1}}
        />
        <StyledButton
          label="Per Type"
          id='per_type'
          contained={!perSortMngr}
          onClick={() => {
            setPerSortMngr(false);
          }}
          sx={{marginRight: 1}}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body1" sx={{
          fontWeight: 'bold'
        }}>Name (ISIN)</Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography variant="body1" sx={{
          fontWeight: 'bold',
          textAlign: 'right'
        }}>Amount</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1" sx={{
          fontWeight: 'bold',
          textAlign: 'right'
        }}>OPP</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1" sx={{
          fontWeight: 'bold',
          textAlign: 'right'
        }}>Last price</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1" sx={{
          fontWeight: 'bold',
          textAlign: 'right'
        }}>Last br.-down</Typography>
      </Grid>
      <Grid item xs={1}>
      </Grid>
      <Grid item xs={12}>
        <Divider />
        <Divider />
      </Grid>

      {!!perSortMngr && (investmentManagers || []).map(manager => <>
        <Grid item xs={12}>
          <Typography variant="h4" sx={{fontWeight: 'bold'}}>{manager.name}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {sortingLines((investments || []).filter(fff => (fff.investment_manager_id || 0) === (manager.id || '')))}
      </>)}
      {!perSortMngr && Object.keys(investmentTypes).sort().map(invType => <>
        <Grid item xs={12}>
          <Typography variant="h4" sx={{fontWeight: 'bold'}}>{investmentTypes[invType]}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {sortingLines((investments || []).filter(fff => (fff.investment_type || '') === invType))}
      </>)}
    </Grid>}
  </Page>;
}
